import React, { useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import posthog from 'posthog-js';
import Accordion, { AccordionSectionType } from '../Accordion';
import SubscribeModal from '../Modal/SubscribeModal';
import Social, { defaultSocialLinks } from '../Social';
import { Paragraph, Label } from '../../ui/typography';
import { NavLink } from '../../ui/atoms/Link/NavLink';
import { getSubscribeModalLocation } from '../../utils/analytics';

import config from '../../../config.json';

const { routes } = config;

const FooterContainer = styled.div`
  background: linear-gradient(180deg, #fff 0%, #f5f4ff 100%);
  width: 100%;
`;

const FooterWrapper = styled.div`
  max-width: ${({ theme }) => theme.containerWidth}px;
  margin: 0 auto;
  padding: 40px 64px 16px 64px;
  margin: 0 auto 0 auto;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    padding: 40px 16px 16px 16px;
  }
`;

const FooterRow = styled.div<{ hideMobile?: boolean; center?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${({ hideMobile }) => hideMobile && `display: none;`}
    ${({ center }) => center && `justify-content: center;`}
  }
  ${({ center }) =>
    center &&
    `
      border-top: 1px solid var(--brand-colors-2023-eclipse-black-partial-33, rgba(44, 0, 115, 0.33));
      padding-top: 16px;
    `}
`;

const FooterSectionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const FooterSection = styled(FooterRow)`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const FooterList = styled.div<{ hideMobile?: boolean }>`
  margin: 48px 0;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${({ hideMobile }) => hideMobile && `display: none;`};
    margin: 24px 0;
  }
`;

const FooterSplit = styled.div<{ first?: boolean }>`
  margin-right: 48px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${({ first }) => !first && `margin-top: -16px;`}
  }
`;

const HideDesktop = styled.div`
  display: none;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    display: inline-block;
  }
`;

const SocialMobile = styled(HideDesktop)`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 42px 0 24px 0;
  }
`;

const getPolicyLinks = () => {
  const policyLinks = (
    <>
      <div key="privacy" className="mr-8 max-md:mb-4">
        <Paragraph size="small">
          <NavLink href={routes.privacyPolicy}>Privacy Policy</NavLink>
        </Paragraph>
      </div>
      <div key="terms" className="mr-8 max-md:mb-4">
        <Paragraph size="small">
          <NavLink href={routes.termsOfService}>Terms of Service</NavLink>
        </Paragraph>
      </div>
      <div key="shipping" className="mr-8 max-md:mb-4">
        <Paragraph size="small">
          <NavLink href={routes.shippingPolicy}>Shipping Policy</NavLink>
        </Paragraph>
      </div>
      <div key="refund" className="mr-8 max-md:mb-4">
        <Paragraph size="small">
          <NavLink href={routes.refundPolicy} target="_blank" rel="noreferrer noopener">
            Refund Policy
          </NavLink>
        </Paragraph>
      </div>
      <div key="cookies" className="mr-8 max-md:mb-4">
        <Paragraph size="small">
          <NavLink href={routes.cookiesPolicy}>Cookies Policy</NavLink>
        </Paragraph>
      </div>
    </>
  );

  return policyLinks;
};

const getFooterData = () => {
  const footerData: AccordionSectionType[] = [
    {
      title: 'Who is Makeship?',
      body: [
        {
          body: (
            <FooterSectionRow>
              <FooterSplit first>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.aboutUs}
                      onClick={() =>
                        posthog.capture('footer_link_click', { link_title: 'About Us', link_href: routes.aboutUs })
                      }
                    >
                      About Us
                    </NavLink>
                  </Paragraph>
                </div>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.launchCampaign}
                      data-testid="launch-campaign-footer-cta"
                      onClick={() =>
                        posthog.capture('footer_link_click', {
                          link_title: 'Launch Campaign',
                          link_href: routes.launchCampaign,
                        })
                      }
                    >
                      Launch Campaign
                    </NavLink>
                  </Paragraph>
                </div>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.howItWorks}
                      onClick={() =>
                        posthog.capture('footer_link_click', {
                          link_title: 'How it Works',
                          link_href: routes.howItWorks,
                        })
                      }
                    >
                      How it Works
                    </NavLink>
                  </Paragraph>
                </div>
              </FooterSplit>
              <FooterSplit>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.makeGood}
                      onClick={() =>
                        posthog.capture('footer_link_click', {
                          link_title: 'Make Good',
                          link_href: routes.makeGood,
                        })
                      }
                    >
                      Make Good
                    </NavLink>
                  </Paragraph>
                </div>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.careers}
                      onClick={() =>
                        posthog.capture('footer_link_click', {
                          link_title: 'Careers',
                          link_href: routes.careers,
                        })
                      }
                    >
                      Careers
                    </NavLink>
                  </Paragraph>
                </div>
                <div className="my-4">
                  <Paragraph>
                    <NavLink
                      href={routes.community}
                      onClick={() =>
                        posthog.capture('footer_link_click', {
                          link_title: 'Community',
                          link_href: routes.community,
                        })
                      }
                    >
                      Community
                    </NavLink>
                  </Paragraph>
                </div>
              </FooterSplit>
            </FooterSectionRow>
          ),
        },
      ],
    },
    {
      title: 'Support',
      body: [
        {
          body: (
            <>
              <div className="my-4">
                <Paragraph>
                  <NavLink
                    href={routes.helpCenter}
                    onClick={() =>
                      posthog.capture('footer_link_click', {
                        link_title: 'Help Center',
                        link_href: routes.helpCenter,
                      })
                    }
                  >
                    Help Center
                  </NavLink>
                </Paragraph>
              </div>
              <div className="my-4">
                <Paragraph>
                  <NavLink
                    href={routes.shippingDestinations}
                    onClick={() =>
                      posthog.capture('footer_link_click', {
                        link_title: 'Shipping Destinations',
                        link_href: routes.shippingDestinations,
                      })
                    }
                  >
                    Shipping Destinations
                  </NavLink>
                </Paragraph>
              </div>
              <div className="my-4">
                <Paragraph>
                  <NavLink
                    target="_blank"
                    href={routes.makeshipAftership}
                    rel="noopener noreferrer"
                    onClick={() =>
                      posthog.capture('footer_link_click', {
                        link_title: 'Track Your Order',
                        link_href: routes.makeshipAftership,
                      })
                    }
                  >
                    Track Your Order
                  </NavLink>
                </Paragraph>
              </div>
              <HideDesktop>{getPolicyLinks()}</HideDesktop>
            </>
          ),
        },
      ],
    },
  ];

  return footerData;
};

const Footer: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  function handleSignInKeyPress(event: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (event.code === 'Space' || event.code === 'Enter') {
      // Prevent spacebar from scrolling page down when focus is on this element
      event.preventDefault();
      setIsModalOpen(true);
    }
  }

  return (
    <>
      <FooterContainer>
        <FooterWrapper>
          <FooterRow>
            <Image src="/assets/footer-logo.png" width={194} height={66} quality={90} alt="makeship logo" />
            <FooterRow hideMobile>
              <Social links={defaultSocialLinks} />
            </FooterRow>
          </FooterRow>
          <FooterSection>
            <FooterList>
              <div className="my-4">
                <Paragraph>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <NavLink
                    onClick={() => {
                      posthog.capture('footer_link_click', {
                        link_title: 'Sign up for Email',
                      });
                      setIsModalOpen(true);
                    }}
                    onKeyDown={(e) => handleSignInKeyPress(e)}
                    tabIndex={0}
                  >
                    Sign up for Email
                  </NavLink>
                </Paragraph>
              </div>
              <div className="my-4">
                <Paragraph>
                  <NavLink
                    href={routes.login}
                    onClick={() =>
                      posthog.capture('footer_link_click', {
                        link_title: 'Sign In/Up',
                        link_href: routes.login,
                      })
                    }
                  >
                    Sign In/Up
                  </NavLink>
                </Paragraph>
              </div>
            </FooterList>
            {getFooterData().map((section) => (
              <FooterList key={section.title} hideMobile>
                <Label size="large" className="mt-4 border-b pb-4">
                  {section.title}
                </Label>
                {section.body.map((body, index) => (
                  <div key={body.title || `body section ${index}`}>{body.body}</div>
                ))}
              </FooterList>
            ))}
            <FooterList hideMobile />
            <HideDesktop>
              <Accordion data={getFooterData()} accordionLocation="footer" />
            </HideDesktop>
          </FooterSection>
          <SocialMobile>
            <Social links={defaultSocialLinks} socialLocation="footer" />
          </SocialMobile>
          <FooterRow center>
            <FooterRow hideMobile>{getPolicyLinks()}</FooterRow>
            <Paragraph size="small">&copy; {new Date().getFullYear()} Makeship Ltd.</Paragraph>
          </FooterRow>
        </FooterWrapper>
      </FooterContainer>
      <SubscribeModal
        isOpen={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
        }}
        tags="footer, subscribed-footer"
        eventLocation={getSubscribeModalLocation()}
      />
    </>
  );
};

export default Footer;
